import ChevronRightIcon from "@assets/icons/new/arrow-right_18px.svg?react";
import CloseIcon from "@assets/icons/new/close_24px.svg?react";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { FaroTextButton } from "@components/common/faro-text-button";
import { SphereDivider } from "@components/common/sphere-divider";
import { BasicTabProps } from "@components/common/tabs/tabs-type";
import { SphereAvatar } from "@components/header/sphere-avatar";
import { neutral, SvgIcon } from "@flat-ui";
import { Box, Grid2, Typography } from "@mui/material";
import { useCloseAccountSettings } from "@pages/account-settings/use-close-account-settings";
import { useUserContext } from "@pages/account-settings/user-provider";
import { AccountSettingsTabs } from "@router/route-params";
import {
  DEFAULT_INPUT_FONT_SIZE,
  DEFAULT_TEXT_FONT_SIZE,
  withEllipsisTwoLines,
} from "@styles/common-styles";
import { OpenTabEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { getPrettyName, getUserInitials } from "@utils/user-utils";
import { LogoutButtonMobile } from "@pages/account-settings/account-settings-layout/mobile-layout/logout-button-mobile";
import { useAppSelector } from "@store/store-helper";
import { isUserLoggedInSelector } from "@store/user/user-selector";

interface Props {
  /** List of available tabs */
  tabs: Record<AccountSettingsTabs, BasicTabProps<AccountSettingsTabs>>;
}

/** The mobile layout for account settings tab selection */
export function AccountSettingsMobileTabSelection({
  tabs,
}: Props): JSX.Element | null {
  const { user } = useUserContext();
  const { onCloseButtonClick } = useCloseAccountSettings();
  const { trackEvent } = useTrackEvent();
  const isUserLoggedIn = useAppSelector(isUserLoggedInSelector);

  if (!user) {
    return null;
  }

  return (
    <>
      {/* Header for the tab selection */}
      <Grid2
        container
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="nowrap"
        sx={{ px: "15px", mt: "45px" }}
      >
        <Grid2 container flexWrap="nowrap" alignItems="center">
          <SphereAvatar
            src={user.thumbnailUrl}
            initials={getUserInitials(user)}
            icon={undefined}
            shouldUseSimplifiedAvatar={false}
            shouldHideWhiteRim={true}
            size="large"
          />
          <Grid2 container direction="column" sx={{ ml: "20px" }}>
            <Typography
              sx={{
                color: neutral[800],
                fontSize: DEFAULT_INPUT_FONT_SIZE,
                ...withEllipsisTwoLines,
              }}
            >
              <var>{getPrettyName(user)}</var>
            </Typography>
            <Typography
              sx={{
                color: neutral[600],
                fontSize: DEFAULT_TEXT_FONT_SIZE,
                ...withEllipsisTwoLines,
              }}
            >
              <var>{user.email}</var>
            </Typography>
          </Grid2>
        </Grid2>
        <SphereIconButton
          component={CloseIcon}
          onClick={onCloseButtonClick}
          iconButtonProps={{
            // Adjust alignment of the close icon in small screen
            sx: {
              width: "2rem",
              height: "2rem",
            },
          }}
        />
      </Grid2>

      {/* Body for the tab selection */}
      <Box sx={{ mt: "40px", mx: "15px" }}>
        {Object.values(tabs).map((tab, index) => (
          <FaroTextButton
            key={index}
            sx={{
              color: neutral[800],
              fontSize: DEFAULT_INPUT_FONT_SIZE,
              p: "8px",
              mb: "8px",
              width: "100%",
              justifyContent: "space-between",
            }}
            endIcon={<SvgIcon source={ChevronRightIcon} />}
            to={tab.routeId}
            onClick={() => {
              trackEvent({
                name: OpenTabEvents.openAccountSettingsTab,
                props: {
                  isUserLoggedIn,
                  tab: tab.routeId,
                },
              });
            }}
          >
            {tab.label}
          </FaroTextButton>
        ))}
        <SphereDivider sx={{ borderColor: neutral[200], my: "10px" }} />
        <LogoutButtonMobile />
      </Box>
    </>
  );
}
